import {
  Divider,
  Form,
  Icon,
  PrimaryButton,
  Space,
  TertiaryButton,
  WithFormControlSelect,
  WithTooltip,
} from '@procurify/ui'
import {
  type FormikErrors,
  type FormikHelpers,
  type FormikTouched,
} from 'formik'

import {
  CountrySelect,
  type CountryOption,
} from '@webapp/core/components/CountrySelect'
import { CurrencySelect } from '@webapp/core/components/CurrencySelect'
import { type Currency } from '@webapp/core/entities/external/Currency'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { BaseCurrencyLabelRowStyled } from './styles/BaseCurrencyLabelRowStyled'
import { FieldRowStyled } from './styles/FieldRowStyled'
import { FieldsStyled } from './styles/FieldsStyled'
import { FormActionsStyled } from './styles/FormActionsStyled'
import { ManageCurrencyListLinkStyled } from './styles/ManageCurrencyListLinkStyled'

export interface LegalEntityFormSchema {
  name: string
  country: Nullable<CountryOption>
  externalId: string
  baseCurrency: Nullable<Currency>
}

export interface ILegalEntityFormProps {
  initialValues?: Partial<LegalEntityFormSchema>
  onContinue: (
    values: LegalEntityFormSchema,
    actions: FormikHelpers<LegalEntityFormSchema>
  ) => void | Promise<void>
  onCancel: () => void
  submitLabel?: string
  disableCountry?: boolean
  disableBaseCurrency?: boolean
  /**
   * Returns true when the given name already belongs to another entity, used to
   * surface an inline "name already in use" error on submit. Names are unique
   * per org.
   */
  isNameTaken?: (name: string) => boolean
  /**
   * Returns true when the given (non-empty) external ID already belongs to
   * another entity, used to surface an inline "ID already in use" error on
   * submit. External IDs are unique per org.
   */
  isExternalIdTaken?: (externalId: string) => boolean
}

const defaultValues: LegalEntityFormSchema = {
  name: '',
  country: null,
  externalId: '',
  baseCurrency: null,
}

const EXTERNAL_ID_MAX_LENGTH = 100

const ALL_FIELDS_TOUCHED = {
  name: true,
  country: true,
  externalId: true,
  baseCurrency: true,
} as unknown as FormikTouched<LegalEntityFormSchema>

export const LegalEntityForm = ({
  initialValues,
  onContinue,
  onCancel,
  submitLabel,
  disableCountry = false,
  disableBaseCurrency = false,
  isNameTaken,
  isExternalIdTaken,
}: ILegalEntityFormProps) => {
  const intl = useProcurifyIntl()

  const validate = (
    values: LegalEntityFormSchema
  ): FormikErrors<LegalEntityFormSchema> => {
    const errors: Partial<Record<keyof LegalEntityFormSchema, string>> = {}

    if (!values.name.trim()) {
      errors.name = intl.formatMessage(messages.nameRequiredError)
    } else if (isNameTaken?.(values.name)) {
      errors.name = intl.formatMessage(messages.nameDuplicateError)
    }

    if (!disableCountry && !values.country) {
      errors.country = intl.formatMessage(messages.countryRequiredError)
    }
    if (!disableBaseCurrency && !values.baseCurrency) {
      errors.baseCurrency = intl.formatMessage(
        messages.baseCurrencyRequiredError
      )
    }
    if (values.externalId.trim().length > EXTERNAL_ID_MAX_LENGTH) {
      errors.externalId = intl.formatMessage(messages.externalIdMaxLengthError)
    } else if (
      values.externalId.trim() &&
      isExternalIdTaken?.(values.externalId)
    ) {
      errors.externalId = intl.formatMessage(messages.externalIdDuplicateError)
    }

    return errors as FormikErrors<LegalEntityFormSchema>
  }

  const handleSubmit = (
    values: LegalEntityFormSchema,
    actions: FormikHelpers<LegalEntityFormSchema>
  ) => {
    const errors = validate(values)

    if (Object.keys(errors).length > 0) {
      actions.setErrors(errors)
      actions.setTouched(ALL_FIELDS_TOUCHED, false)
      actions.setSubmitting(false)

      return
    }

    return onContinue(values, actions)
  }

  const externalIdLabel = (
    <span>
      {intl.formatMessage(messages.externalIdLabel)}
      <WithTooltip
        title={intl.formatMessage(messages.externalIdTooltip)}
        placement='top'
      >
        <Icon icon='question' ml={0.25} />
      </WithTooltip>
    </span>
  )

  const baseCurrencyLabel = (
    <BaseCurrencyLabelRowStyled>
      <span>
        {intl.formatMessage(messages.baseCurrencyLabel)}
        <WithTooltip
          title={intl.formatMessage(messages.baseCurrencyTooltip)}
          placement='top'
        >
          <Icon icon='question' ml={0.25} />
        </WithTooltip>
      </span>
      <ManageCurrencyListLinkStyled href={'/#/settings/currency/active'}>
        {intl.formatMessage(messages.manageCurrencyLink)}
      </ManageCurrencyListLinkStyled>
    </BaseCurrencyLabelRowStyled>
  )

  return (
    <Form<LegalEntityFormSchema>
      initialValues={{ ...defaultValues, ...initialValues }}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form.Content noValidate>
          <FieldsStyled>
            <FieldRowStyled>
              <Space direction='horizontal' size='medium' align='start'>
                <Space.Item width={1 / 2}>
                  <Form.TextField
                    id='name'
                    name='name'
                    label={intl.formatMessage(messages.nameLabel)}
                    placeholder={intl.formatMessage(messages.namePlaceholder)}
                    autoFocus
                  />
                </Space.Item>

                <Space.Item width={1 / 2}>
                  <WithFormControlSelect
                    id='country'
                    name='country'
                    label={intl.formatMessage(messages.countryLabel)}
                    disabled={disableCountry}
                  >
                    <CountrySelect />
                  </WithFormControlSelect>
                </Space.Item>
              </Space>
            </FieldRowStyled>

            <FieldRowStyled>
              <Space direction='horizontal' size='medium' align='start'>
                <Space.Item width={1 / 2}>
                  <Form.TextField
                    id='externalId'
                    name='externalId'
                    label={externalIdLabel}
                  />
                </Space.Item>

                <Space.Item width={1 / 2}>
                  <WithFormControlSelect
                    id='baseCurrency'
                    name='baseCurrency'
                    label={baseCurrencyLabel}
                    disabled={disableBaseCurrency}
                  >
                    <CurrencySelect />
                  </WithFormControlSelect>
                </Space.Item>
              </Space>
            </FieldRowStyled>
          </FieldsStyled>

          <Divider space={0} />

          <FormActionsStyled>
            <TertiaryButton
              type='button'
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {intl.formatMessage(messages.cancelButton)}
            </TertiaryButton>
            <PrimaryButton type='submit' disabled={isSubmitting}>
              {submitLabel ?? intl.formatMessage(messages.createButton)}
            </PrimaryButton>
          </FormActionsStyled>
        </Form.Content>
      )}
    </Form>
  )
}

LegalEntityForm.displayName = 'LegalEntityForm'
